import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { marked } from "marked"

// marked 설정 - 구독자 채팅과 동일
marked.use({
  renderer: {
    del(token) {
      return token.text
    },
    hr() {
      return '<hr class="my-4 border-t border-gray-300 dark:border-gray-600">'
    }
  }
})
marked.setOptions({
  breaks: true,
  gfm: true
})

export default class extends Controller {
  static values = {
    sessionId: String,
    avatarUrl: String,
    resetSessionUrl: String
  }

  static targets = [
    "messages",
    "emptyState",
    "form",
    "input",
    "submitButton",
    "sendIcon",
    "loadingIcon",
    "scrollContainer",
    "aiResponseMessage",
    "streamingContent",
    "messageContainer",
    "resetSection"
  ]

  connect() {
    this.currentMessageId = null
    this.isStreaming = false
    this.streamingContent = ""

    // ActionCable 구독
    this.subscription = createConsumer().subscriptions.create(
      { channel: "AiChatChannel", session_id: this.sessionIdValue },
      {
        connected: () => console.log("Platform AI Chat connected"),
        disconnected: () => console.log("Platform AI Chat disconnected"),
        received: (data) => this.handleMessage(data)
      }
    )

    // 기존 메시지들의 마크다운 렌더링
    this.renderExistingMessages()

    // 스크롤을 맨 아래로
    this.scrollToBottom()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  renderExistingMessages() {
    this.aiResponseMessageTargets.forEach(el => {
      const rawContent = el.textContent
      if (rawContent) {
        el.innerHTML = this.formatContent(rawContent)
      }
      el.classList.remove('opacity-0')
      el.classList.add('opacity-100', 'transition-opacity', 'duration-150')
    })
  }

  handleMessage(data) {
    switch(data.type) {
      case 'stream_start':
        this.startStreaming(data)
        break
      case 'chunk':
        this.appendChunk(data)
        break
      case 'stream_end':
        this.endStreaming(data)
        break
      case 'stream_error':
        this.handleStreamError(data)
        break
    }
  }

  startStreaming(data) {
    this.isStreaming = true
    this.currentMessageId = data.message_id
    this.streamingContent = ""

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.remove()
    }

    this.removePendingMessage()

    const avatarHtml = this.buildAvatarHtml()

    const messageHtml = `
      <div id="message-${data.message_id}" class="flex justify-start" data-role="chat_response">
        <div class="max-w-[95%] order-1">
          <div class="flex items-start gap-2">
            <div class="flex-shrink-0">
              ${avatarHtml}
            </div>
            <div class="flex-1 min-w-0" data-platform-ai-chat-target="messageContainer">
              <div class="bg-white dark:bg-gray-800 rounded-xl rounded-tl-sm px-3 py-2 shadow-sm border border-gray-100 dark:border-gray-700">
                <div class="prose prose-sm dark:prose-invert max-w-none text-sm whitespace-normal" data-platform-ai-chat-target="streamingContent">
                  <span class="inline-flex gap-1 py-1">
                    <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                    <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                    <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    this.messagesTarget.insertAdjacentHTML('beforeend', messageHtml)
    this.scrollToBottom()
  }

  appendChunk(data) {
    if (!this.hasStreamingContentTarget) return

    this.streamingContent += data.content

    if (this.streamingContent.trim()) {
      this.streamingContentTarget.innerHTML = this.formatContent(this.streamingContent)
    }

    this.scrollToBottom()
  }

  endStreaming(data) {
    this.isStreaming = false
    this.currentMessageId = null

    if (!this.hasStreamingContentTarget) return

    const typingIndicator = this.streamingContentTarget.querySelector('.inline-flex')
    if (typingIndicator) {
      typingIndicator.remove()
    }

    this.streamingContentTarget.innerHTML = this.formatContent(data.content)

    // 관련 가이드 링크 표시
    if (data.guide_links && data.guide_links.length > 0) {
      const guideLinksHtml = this.renderGuideLinks(data.guide_links)
      this.streamingContentTarget.insertAdjacentHTML('afterend', guideLinksHtml)
    }

    this.streamingContentTarget.dataset.platformAiChatTarget = 'aiResponseMessage'

    // 타임스탬프 추가
    if (this.hasMessageContainerTarget) {
      const actionsHtml = `
        <div class="mt-1.5 flex items-center gap-2 ml-1">
          <span class="text-xs text-gray-400 dark:text-gray-500">
            ${new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      `
      this.messageContainerTarget.insertAdjacentHTML('beforeend', actionsHtml)

      // 빈 응답으로 끝난 경우 서버가 원래 질문을 함께 내려준다. (#532)
      // target 속성을 지우면 hasMessageContainerTarget 이 false 가 되므로 지우기 전에 붙인다.
      if (data.retry_content) {
        this.appendRetryButton(this.messageContainerTarget, data.retry_content)
      }

      delete this.messageContainerTarget.dataset.platformAiChatTarget
    }

    this.setLoading(false)
    this.scrollToBottom()
    this.showResetSection()
  }

  // 오류·빈 응답 뒤에 질문을 다시 타이핑하지 않고 같은 요청을 재발행하는 버튼. (#569)
  //
  // 질문 원문을 문자열 템플릿으로 끼워 넣지 않는다. escapeHtml 은 큰따옴표를 이스케이프하지
  // 않아서 따옴표가 든 질문이 속성 밖으로 새어 나간다. dataset 으로 직접 넣는다.
  appendRetryButton(container, content) {
    const wrapper = document.createElement('div')
    wrapper.className = 'mt-1.5 ml-1'
    wrapper.dataset.retryBlock = ''

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-600 rounded px-2 py-1'
    button.dataset.action = 'platform-ai-chat#retryMessage'
    button.dataset.content = content
    button.innerHTML = `
      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
      </svg>
    `
    button.appendChild(document.createTextNode('다시 시도'))

    wrapper.appendChild(button)
    container.appendChild(wrapper)
  }

  retryMessage(event) {
    event.preventDefault()

    const content = event.currentTarget.dataset.content
    if (!content || this.isStreaming) return

    event.currentTarget.closest('[data-retry-block]')?.remove()

    this.inputTarget.value = content
    this.sendMessage(event)
  }

  renderGuideLinks(guideLinks) {
    if (!guideLinks || guideLinks.length === 0) return ''

    const links = guideLinks.slice(0, 5).map(link => {
      return `
        <a href="${this.escapeHtml(link.url)}"
           target="_blank"
           class="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          ${this.escapeHtml(this.truncateText(link.title, 30))}
        </a>
      `
    }).join('')

    return `
      <div class="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <p class="text-xs text-gray-400 dark:text-gray-500 mb-1">관련 가이드:</p>
        <div class="flex flex-wrap gap-1">
          ${links}
        </div>
      </div>
    `
  }

  handleStreamError(data) {
    this.isStreaming = false
    this.currentMessageId = null

    this.removePendingMessage()

    if (this.hasStreamingContentTarget) {
      this.streamingContentTarget.innerHTML = `<span class="text-red-500">${this.escapeHtml(data.content)}</span>`
      delete this.streamingContentTarget.dataset.platformAiChatTarget
    }

    if (this.hasMessageContainerTarget) {
      if (data.retry_content) {
        this.appendRetryButton(this.messageContainerTarget, data.retry_content)
      }
      delete this.messageContainerTarget.dataset.platformAiChatTarget
    }

    this.setLoading(false)
    this.scrollToBottom()
  }

  sendMessage(event) {
    event.preventDefault()

    const content = this.inputTarget.value.trim()
    if (!content || this.isStreaming) return

    this.setLoading(true)
    this.inputTarget.value = ''
    this.autoResize()

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.remove()
    }

    // 사용자 메시지 즉시 표시
    const userMessageHtml = `
      <div class="flex justify-end" data-role="user">
        <div class="max-w-[95%] order-2">
          <div class="flex items-start gap-2 justify-end">
            <div class="flex-1 text-right">
              <div class="inline-block bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl rounded-tr-sm px-3 py-2 text-left">
                <div class="text-sm whitespace-normal">${this.escapeHtml(content)}</div>
              </div>
              <div class="mt-0.5 text-xs text-gray-400 dark:text-gray-500 mr-1">
                ${new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        </div>
      </div>
    `
    this.messagesTarget.insertAdjacentHTML('beforeend', userMessageHtml)
    this.scrollToBottom()

    // AI 응답 대기 중 로딩 표시
    this.showPendingMessage()

    // 서버에 메시지 전송
    // CSRF 토큰은 embed 폼의 hidden input(authenticity_token)을 사용한다.
    // 게스트의 경우 embed 액션이 새 세션을 만들 수 있으므로 부모 페이지의 meta
    // 토큰은 다른 세션의 토큰일 수 있어 검증에 실패한다.
    const csrfToken = this.formTarget.querySelector('input[name="authenticity_token"]')?.value
      || document.querySelector('[name="csrf-token"]')?.content
    const url = this.formTarget.action
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: new URLSearchParams({ content: content, session_id: this.sessionIdValue })
    }).then(async response => {
      if (!response.ok) {
        this.removePendingMessage()
        this.setLoading(false)
        // 속도 제한(429)은 서버가 내려준 안내 문구를 그대로 노출한다.
        let message = '메시지 전송에 실패했습니다. 잠시 후 다시 시도해주세요.'
        if (response.status === 429) {
          const data = await response.json().catch(() => null)
          message = data?.error || '짧은 시간에 너무 많은 메시지를 보냈어요. 잠시 후 다시 시도해주세요.'
        }
        this.showErrorMessage(message)
      }
    }).catch(error => {
      console.error('Failed to send message:', error)
      this.removePendingMessage()
      this.setLoading(false)
      this.showErrorMessage('네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.')
    })
  }

  showErrorMessage(message) {
    const avatarHtml = this.buildAvatarHtml()

    const errorMessageHtml = `
      <div class="flex justify-start" data-role="chat_response">
        <div class="max-w-[95%] order-1">
          <div class="flex items-start gap-2">
            <div class="flex-shrink-0">
              ${avatarHtml}
            </div>
            <div class="flex-1 min-w-0">
              <div class="bg-red-50 dark:bg-red-900/20 rounded-xl rounded-tl-sm px-3 py-2 shadow-sm border border-red-200 dark:border-red-800">
                <div class="text-sm text-red-600 dark:text-red-400">
                  ${this.escapeHtml(message)}
                </div>
              </div>
              <div class="mt-0.5 text-xs text-gray-400 dark:text-gray-500 ml-1">
                ${new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    this.messagesTarget.insertAdjacentHTML('beforeend', errorMessageHtml)
    this.scrollToBottom()
  }

  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault()
      this.sendMessage(event)
    }
  }

  autoResize() {
    const textarea = this.inputTarget
    textarea.style.height = 'auto'
    textarea.style.height = Math.min(textarea.scrollHeight, 100) + 'px'
  }

  setLoading(loading) {
    this.submitButtonTarget.disabled = loading

    if (loading) {
      this.sendIconTarget.classList.add('hidden')
      this.loadingIconTarget.classList.remove('hidden')
    } else {
      this.sendIconTarget.classList.remove('hidden')
      this.loadingIconTarget.classList.add('hidden')
    }
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      if (this.hasScrollContainerTarget) {
        this.scrollContainerTarget.scrollTop = this.scrollContainerTarget.scrollHeight
      }
    })
  }

  formatContent(content) {
    if (!content) return ''

    let html = marked.parse(content).trim()

    // 테이블을 스크롤 가능한 wrapper로 감싸기
    html = html.replace(/<table>/g, '<div class="table-scroll-wrapper"><table>')
    html = html.replace(/<\/table>/g, '</table></div>')

    return html
  }

  showPendingMessage() {
    const avatarHtml = this.buildAvatarHtml()

    const pendingMessageHtml = `
      <div id="pending-platform-ai-message" class="flex justify-start" data-role="chat_response">
        <div class="max-w-[95%] order-1">
          <div class="flex items-start gap-2">
            <div class="flex-shrink-0">
              ${avatarHtml}
            </div>
            <div class="flex-1 min-w-0">
              <div class="bg-white dark:bg-gray-800 rounded-xl rounded-tl-sm px-3 py-2 shadow-sm border border-gray-100 dark:border-gray-700">
                <div class="prose prose-sm dark:prose-invert max-w-none text-sm whitespace-normal">
                  <span class="inline-flex gap-1 py-1">
                    <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                    <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                    <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    this.messagesTarget.insertAdjacentHTML('beforeend', pendingMessageHtml)
    this.scrollToBottom()
  }

  removePendingMessage() {
    const pendingMessage = document.getElementById('pending-platform-ai-message')
    if (pendingMessage) {
      pendingMessage.remove()
    }
  }

  buildAvatarHtml() {
    if (this.avatarUrlValue) {
      return `
        <div class="w-6 h-6 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 flex items-center justify-center">
          <img src="${this.avatarUrlValue}" class="w-3.5 h-3.5" style="width: 14px; height: 14px;">
        </div>
      `
    }
    return `
      <div class="w-6 h-6 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 flex items-center justify-center text-white text-xs font-bold">
        AI
      </div>
    `
  }

  truncateText(str, maxLength) {
    if (!str) return ''
    return str.length > maxLength ? str.slice(0, maxLength) + '...' : str
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // 채팅 세션 초기화 (새 채팅 시작)
  async resetSession() {
    if (!this.resetSessionUrlValue) return

    if (!confirm('현재 대화를 종료하고 새 채팅을 시작하시겠습니까?')) {
      return
    }

    try {
      const response = await fetch(this.resetSessionUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('세션 초기화 실패')
      }

      const data = await response.json()

      if (data.success) {
        const frame = document.getElementById('platform-floating-ai-chat')
        // embed_url이 프레임의 현재 src와 동일(session_id 미포함)해서
        // frame.src 재할당만으로는 Turbo가 재fetch를 건너뛴다.
        // reload()는 src=null→src 재설정으로 동일 URL도 강제 재로딩한다.
        if (frame && typeof frame.reload === 'function') {
          frame.reload()
        } else if (frame && data.embed_url) {
          frame.src = data.embed_url
        } else {
          window.location.reload()
        }
      }
    } catch (error) {
      console.error('Reset session error:', error)
    }
  }

  showResetSection() {
    if (this.hasResetSectionTarget) {
      this.resetSectionTarget.classList.remove('hidden')
    }
  }
}
